import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { ApiError } from '@/api/client'
import { useFieldOptions, useRecipe, useSaveRecipe } from '@/api/hooks'
import type { RecipeDetail, ResolvedRecipe } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { CardSection } from '@/components/ui/Card'
import { Note } from '@/components/ui/Note'
import { ErrorNote, FormError, PageLoader } from '@/components/ui/Feedback'
import { Input, NumberInput, Select, Textarea } from '@/components/ui/Field'
import { ConfirmDialog } from '@/components/ui/Overlay'
import { IngredientLine, LINE_COLS, type LineDraft } from './IngredientLine'
import { isTracked } from '@/lib/macros'
import { importedRecipe, markImportSaved } from '@/features/import/session'
import { buttonClasses } from '@/components/ui/buttonStyles'
import { PreparationEditor } from './PreparationEditor'
import { PageShell } from '@/components/layout/Page'
import { RecipeImageField } from './RecipeImageField'
import { TagEditor } from './TagEditor'
import { cn } from '@/lib/cn'
import { formatGrams } from '@/lib/format'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { ArrowLeft, Plus, Save } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom'

let lineCounter = 0
function nextKey(): string {
  lineCounter += 1
  return `line-${lineCounter}`
}

const EFFORT_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: '1', label: 'Low' },
  { value: '2', label: 'Medium' },
  { value: '3', label: 'High' },
]

/**
 * Data fetching happens here; the form itself only mounts once it has something
 * to show, which lets it initialise its state in one go instead of syncing
 * state to an effect afterwards.
 */
export function RecipeFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const params = useParams<{ id: string }>()
  const [search] = useSearchParams()
  const recipeId = mode === 'edit' ? Number(params.id) : null
  const existing = useRecipe(recipeId ?? 0)
  const importIndex = mode === 'create' ? search.get('import') : null
  const imported = useMemo(() => {
    if (importIndex === null) return null
    const index = Number(importIndex)
    const recipe = Number.isInteger(index) ? importedRecipe(index) : null
    return recipe ? { index, recipe } : null
  }, [importIndex])

  useDocumentTitle(
    mode === 'create'
      ? 'New recipe'
      : existing.data
        ? `Editing ${existing.data.recipe.name}`
        : existing.isError
          ? 'Recipe not found'
          : 'Edit recipe',
  )

  if (mode === 'create') {
    return imported ? (
      <RecipeFormFields key={`import-${imported.index}`} mode="create" imported={imported} />
    ) : (
      <RecipeFormFields key="new" mode="create" />
    )
  }
  if (existing.isPending) {
    return (
      <PageShell>
        <PageLoader label="Loading recipe…" />
      </PageShell>
    )
  }
  if (existing.isError || !existing.data) {
    const message =
      existing.error instanceof ApiError
        ? existing.error.message
        : 'Could not load the recipe.'
    return (
      <PageShell>
        <ErrorNote
          title="Recipe not found"
          body={message}
          onRetry={() => void existing.refetch()}
        />
      </PageShell>
    )
  }
  return (
    <RecipeFormFields
      key={`recipe-${existing.data.recipe.id}`}
      mode="edit"
      detail={existing.data}
    />
  )
}

function RecipeFormFields({
  mode,
  detail,
  imported,
}: {
  mode: 'create' | 'edit'
  detail?: RecipeDetail
  /** A recipe from the import in progress, to review before saving. */
  imported?: { index: number; recipe: ResolvedRecipe }
}) {
  const navigate = useNavigate()
  const fieldOptions = useFieldOptions()
  const save = useSaveRecipe()

  const initial = useMemo(
    () => (imported ? buildInitialFromImport(imported.recipe) : buildInitial(detail)),
    [detail, imported],
  )

  const [name, setName] = useState(initial.name)
  const [servings, setServings] = useState<number | null>(initial.servings)
  const [cuisine, setCuisine] = useState(initial.cuisine)
  const [proteinBase, setProteinBase] = useState(initial.proteinBase)
  const [tags, setTags] = useState(initial.tags)
  const [vegetarian, setVegetarian] = useState(initial.vegetarian)
  const [preparations, setPreparations] = useState<string[]>(initial.preparations)
  const [rating, setRating] = useState<number | null>(initial.rating)
  const [effort, setEffort] = useState(initial.effort)
  const [notes, setNotes] = useState(initial.notes)
  const [instructions, setInstructions] = useState(initial.instructions)
  const [sourceUrl, setSourceUrl] = useState(initial.sourceUrl)
  const [lines, setLines] = useState<LineDraft[]>(initial.lines)

  const current: InitialState = {
    name,
    servings,
    cuisine,
    proteinBase,
    tags,
    vegetarian,
    preparations,
    rating,
    effort,
    notes,
    instructions,
    sourceUrl,
    lines,
  }

  const recipeId = detail?.recipe.id
  const parent = imported
    ? '/recipes/import'
    : recipeId === undefined
      ? '/recipes'
      : `/recipes/${recipeId}`
  const chosenIds = useMemo(
    () => new Set(lines.flatMap((line) => (line.ingredient ? [line.ingredient.id] : []))),
    [lines],
  )
  const lineKeys = useMemo(() => lines.map((line) => line.key), [lines])

  // Mouse and touch get their own sensors: a pointer sensor would fire on
  // touch first and turn every scroll that started on the grip into a drag.
  // Touch needs a long press so a swipe still scrolls the list.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Leaving with edits still in the form throws them away, so ask first. The
  // baseline is the form as loaded and never moves; `saved` is a ref because a
  // save has to release the blocker on the same tick it navigates, before React
  // gets a chance to re-render.
  const [untouched] = useState(() => signature(initial))
  const saved = useRef(false)
  const dirty = signature(current) !== untouched
  const blocker = useBlocker(() => dirty && !saved.current)

  function addLine() {
    setLines((current) => [
      ...current,
      {
        key: nextKey(),
        ingredient: null,
        amount: null,
        tracked: false,
        displayAmount: null,
        displayUnit: '',
        note: '',
      },
    ])
  }

  function updateLine(key: string, patch: Partial<LineDraft>) {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    )
  }

  function removeLine(key: string) {
    setLines((current) => current.filter((line) => line.key !== key))
  }

  /**
   * Move a line to another slot. Order is the order they are saved in, so a
   * drag here is the same edit the old arrow buttons made.
   */
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setLines((current) => {
      const from = current.findIndex((line) => line.key === active.id)
      const to = current.findIndex((line) => line.key === over.id)
      if (from < 0 || to < 0) return current
      return arrayMove(current, from, to)
    })
  }

  const missingIngredients = lines.filter((line) => !line.ingredient).length
  const totalGrams = lines.reduce((sum, line) => sum + (line.amount ?? 0), 0)
  const filledLines = lines.filter((line) => line.ingredient)
  const unweighed = lines.filter(
    (line) => line.ingredient && line.tracked && line.amount === null,
  )

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const payload: Record<string, unknown> = {
      name: name.trim(),
      servings: servings ?? 5,
      cuisine: cuisine.trim() || null,
      protein_base: proteinBase.trim() || null,
      tags,
      vegetarian,
      preparations,
      notes: notes.trim() || null,
      rating,
      effort_level: effort ? Number(effort) : null,
      instructions: instructions.trim() || null,
      source_url: sourceUrl.trim() || null,
      ingredients: lines.flatMap((line) =>
        line.ingredient
          ? [
              {
                ingredient_id: line.ingredient.id,
                amount_in_grams: line.amount,
                display_amount: line.displayAmount,
                display_unit: line.displayUnit.trim() || null,
                note: line.note.trim() || null,
              },
            ]
          : [],
      ),
    }
    const id = await save.mutateAsync(
      recipeId === undefined ? payload : { id: recipeId, ...payload },
    )
    saved.current = true
    // Replace, never push: the browser Back button must not land on a stale
    // copy of the form we just saved from. An import goes back to its list
    // while recipes are still waiting there.
    const moreToReview = imported ? markImportSaved(imported.index, id) : false
    navigate(moreToReview ? '/recipes/import' : `/recipes/${id}`, { replace: true })
  }

  const unresolved = lines.filter((line) => !line.ingredient && line.imported).length
  const nameConflict =
    imported?.recipe.name_conflict != null &&
    name.trim().toLowerCase() === imported.recipe.recipe.name.trim().toLowerCase()
      ? imported.recipe.name_conflict
      : null

  const error = save.error
  const message =
    error instanceof ApiError ? error.message : error ? 'Could not save the recipe.' : null

  /** Every option already in use or seeded, minus this recipe's current pick. */
  const suggestions = (options: string[] | undefined, current: string): string[] =>
    (options ?? []).filter((option) => option !== current)

  const ratingField = (
    <div className="flex flex-col gap-1.5">
      <span className="text-label font-medium text-ink-soft">Rating</span>
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            aria-label={`Rate ${value} out of 5`}
            aria-pressed={rating === value}
            onClick={() => setRating(rating === value ? null : value)}
            className="grid size-8 place-items-center rounded-md text-lg leading-none transition-colors hover:bg-surface-sunken"
          >
            <span
              aria-hidden
              className={rating !== null && value <= rating ? 'text-caution' : 'text-line-strong'}
            >
              ★
            </span>
          </button>
        ))}
        {rating !== null ? (
          <button
            type="button"
            onClick={() => setRating(null)}
            className="ml-1 text-xs text-ink-faint hover:text-ink"
          >
            Clear
          </button>
        ) : null}
      </div>
    </div>
  )

  return (
    <form onSubmit={submit} className="min-h-full">
      {/* Sticky bar: Save and Back stay reachable without scrolling. */}
      <div className="sticky top-0 z-20 border-b border-line bg-canvas/85 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(parent)}>
              <ArrowLeft size={14} aria-hidden />
              Back
            </Button>
            <span className="hidden truncate text-sm font-medium text-ink sm:inline">
              {imported ? 'Review import' : mode === 'create' ? 'New recipe' : name || 'Untitled recipe'}
            </span>
          </div>
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            disabled={unweighed.length > 0}
          >
            <Save size={16} aria-hidden />
            Save
          </Button>
        </div>
      </div>

      <PageShell className="space-y-5">
        {message ? <FormError>{message}</FormError> : null}

        {imported ? (
          <div className="space-y-2">
            <Note tone={unresolved > 0 ? 'caution' : 'info'}>
              Imported draft. Check the amounts, then save.{' '}
              {unresolved > 0
                ? `${unresolved} ${unresolved === 1 ? 'line needs' : 'lines need'} an ingredient; lines left without one are skipped.`
                : 'Every line has an ingredient.'}
            </Note>
            {nameConflict !== null ? (
              <Note tone="caution">
                A recipe with this name already exists (
                <Link to={`/recipes/${nameConflict}`} className="underline">
                  open it
                </Link>
                ). Rename this one to save it.
              </Note>
            ) : null}
          </div>
        ) : null}

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="min-w-0 space-y-5">
            <CardSection>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  wrapperClassName="sm:col-span-2"
                  className="h-11 text-base font-medium"
                  required
                  autoFocus={mode === 'create'}
                />
                <Combobox
                  label="Cuisine"
                  value={cuisine}
                  onChange={setCuisine}
                  options={suggestions(fieldOptions.data?.cuisines, cuisine)}
                  placeholder="Not set"
                />
                <Combobox
                  label="Protein base"
                  value={proteinBase}
                  onChange={setProteinBase}
                  options={suggestions(fieldOptions.data?.protein_bases, proteinBase)}
                  placeholder="Not set"
                />
                <div className="sm:col-span-2">
                  <TagEditor
                    tags={tags}
                    suggestions={fieldOptions.data?.tags ?? []}
                    onChange={setTags}
                  />
                </div>
              </div>
            </CardSection>

            <CardSection
              title="Ingredients"
              subtitle="Grams drive the nutrition. Quantity and unit are how the recipe reads."
            >
              {lines.length === 0 ? (
                <p className="rounded-[var(--radius-field)] border border-dashed border-line-strong px-3 py-6 text-center text-sm text-pretty text-ink-faint">
                  No ingredients yet.
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                  accessibility={{
                    screenReaderInstructions: {
                      draggable:
                        'Press space to lift the ingredient line, the arrow keys to move it, space to drop it and escape to cancel.',
                    },
                  }}
                >
                  <div className="-mx-1 overflow-hidden rounded-[var(--radius-field)] border border-line">
                    <div
                      className={cn(
                        LINE_COLS,
                        'hidden border-b border-line bg-surface-sunken px-2 py-1.5 text-xs font-medium text-ink-faint md:grid',
                      )}
                    >
                      <span />
                      <span className="px-2">Ingredient</span>
                      <span className="px-2">Qty</span>
                      <span className="px-2">Unit</span>
                      <span className="px-2">Grams</span>
                      <span className="px-2">Note</span>
                      <span />
                    </div>
                    <SortableContext items={lineKeys} strategy={verticalListSortingStrategy}>
                      <div className="divide-y divide-line">
                        {lines.map((line, index) => (
                          <IngredientLine
                            key={line.key}
                            line={line}
                            index={index}
                            chosenIds={chosenIds}
                            onChange={updateLine}
                            onRemove={removeLine}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>
                </DndContext>
              )}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <Button variant="secondary" size="sm" onClick={addLine}>
                  <Plus size={14} aria-hidden />
                  Add ingredient
                </Button>
                {filledLines.length > 0 ? (
                  <p className="tabular text-xs text-ink-faint">
                    {filledLines.length} {filledLines.length === 1 ? 'ingredient' : 'ingredients'} ·{' '}
                    {formatGrams(totalGrams)} total
                  </p>
                ) : null}
              </div>

              {missingIngredients > 0 ? (
                <Note tone="caution" className="mt-3">
                  {missingIngredients === 1
                    ? 'One empty line will be skipped when saving.'
                    : `${missingIngredients} empty lines will be skipped when saving.`}
                </Note>
              ) : null}

              {unweighed.length > 0 ? (
                <Note tone="caution" className="mt-3">
                  Add grams for {unweighed.map((line) => line.ingredient?.name).join(', ')} to
                  save. {unweighed.length === 1 ? 'It has' : 'They have'} nutrition data, so the
                  totals need a weight.
                </Note>
              ) : null}
            </CardSection>

            <CardSection title="Notes and instructions">
              <div className="grid gap-4">
                <Textarea
                  label="Notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  placeholder="Anything worth remembering about this recipe."
                />
                <Textarea
                  label="Instructions"
                  value={instructions}
                  onChange={(event) => setInstructions(event.target.value)}
                  rows={10}
                  hint="Markdown works: - for steps, **bold**, ## for headings."
                  placeholder={
                    '## Method\n\n- Finely dice the onions.\n- Sear the beef for 4 minutes.'
                  }
                />
              </div>
            </CardSection>
          </div>

          <aside className="min-w-0 space-y-5">
            <CardSection title="Details">
              <div className="grid gap-4">
                <NumberInput
                  label="Servings"
                  value={servings}
                  onValueChange={setServings}
                  min={1}
                  hint="Nutrition is shown per serving."
                  required
                />
                {ratingField}
                <Select
                  label="Effort"
                  value={effort}
                  onChange={(event) => setEffort(event.target.value)}
                  options={EFFORT_OPTIONS}
                />
                <label className="flex cursor-pointer items-center justify-between gap-2">
                  <span className="text-sm font-medium text-ink">Vegetarian</span>
                  <input
                    type="checkbox"
                    checked={vegetarian}
                    onChange={(event) => setVegetarian(event.target.checked)}
                    className="size-4 rounded"
                  />
                </label>
              </div>
            </CardSection>

            <CardSection title="Photo and source">
              <div className="grid gap-4">
                {recipeId === undefined ? (
                  <p className="text-sm text-pretty text-ink-faint">
                    You can add a photo once the recipe is saved.
                  </p>
                ) : (
                  <RecipeImageField
                    recipeId={recipeId}
                    imageUrl={detail?.recipe.image_url ?? null}
                  />
                )}
                <Input
                  label="Source link"
                  type="url"
                  value={sourceUrl}
                  onChange={(event) => setSourceUrl(event.target.value)}
                  placeholder="https://…"
                />
              </div>
            </CardSection>

            <CardSection title="Preparations" subtitle="Each day you cooked it.">
              <PreparationEditor dates={preparations} onChange={setPreparations} />
            </CardSection>
          </aside>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
          <Link to={parent} className={buttonClasses('ghost')}>
            Cancel
          </Link>
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            disabled={unweighed.length > 0}
          >
            <Save size={16} aria-hidden />
            Save
          </Button>
        </div>
      </PageShell>

      {blocker.state === 'blocked' ? (
        <ConfirmDialog
          open
          onClose={() => blocker.reset()}
          onConfirm={() => blocker.proceed()}
          title="Discard unsaved changes?"
          body={<p>Your changes to this recipe have not been saved yet.</p>}
          confirmLabel="Discard changes"
          destructive
        />
      ) : null}
    </form>
  )
}

interface InitialState {
  name: string
  servings: number | null
  cuisine: string
  proteinBase: string
  tags: string[]
  vegetarian: boolean
  preparations: string[]
  rating: number | null
  effort: string
  notes: string
  instructions: string
  sourceUrl: string
  lines: LineDraft[]
}

function buildInitial(detail?: RecipeDetail): InitialState {
  const recipe = detail?.recipe
  return {
    name: recipe?.name ?? '',
    servings: recipe?.servings ?? 5,
    cuisine: recipe?.cuisine ?? '',
    proteinBase: recipe?.protein_base ?? '',
    tags: recipe?.tags ?? [],
    vegetarian: recipe?.vegetarian ?? false,
    preparations: detail?.preparations ?? [],
    rating: recipe?.rating ?? null,
    effort: recipe?.effort_level ? String(recipe.effort_level) : '',
    notes: recipe?.notes ?? '',
    instructions: recipe?.instructions ?? '',
    sourceUrl: recipe?.source_url ?? '',
    lines:
      detail?.ingredients.map((line) => ({
        key: nextKey(),
        ingredient: {
          id: line.ingredient_id,
          name: line.ingredient_name,
          calories: null,
          fat: null,
          carbs: null,
          protein: null,
          fiber: null,
          conversion_unit: null,
          grams_per_unit: null,
          recipe_count: 0,
        },
        amount: line.amount_in_grams,
        // The detail knows which lines need weighing; the synthetic ingredient
        // above deliberately carries no macros, so it cannot answer this.
        tracked: line.tracked,
        displayAmount: line.display_amount,
        displayUnit: line.display_unit ?? '',
        note: line.note ?? '',
      })) ?? [],
  }
}

/** The form as an import proposed it. Lines that need a decision stay open. */
function buildInitialFromImport(resolved: ResolvedRecipe): InitialState {
  const { recipe } = resolved
  return {
    name: recipe.name,
    servings: recipe.servings,
    cuisine: recipe.cuisine ?? '',
    proteinBase: recipe.protein_base ?? '',
    tags: recipe.tags,
    vegetarian: recipe.vegetarian,
    preparations: [],
    rating: recipe.rating ?? null,
    effort: recipe.effort_level ? String(recipe.effort_level) : '',
    notes: recipe.notes ?? '',
    instructions: recipe.instructions ?? '',
    sourceUrl: recipe.source_url ?? '',
    lines: resolved.lines.map((resolvedLine) => {
      const { line, ingredient, issues } = resolvedLine
      const duplicate = issues.includes('duplicate')
      const chosen = resolvedLine.status === 'matched' && !duplicate ? ingredient : null
      return {
        key: nextKey(),
        ingredient: chosen,
        amount: line.amount_in_grams ?? null,
        tracked: isTracked(chosen),
        displayAmount: line.display_amount ?? null,
        displayUnit: line.display_unit ?? '',
        note: line.note ?? '',
        ...(chosen
          ? {}
          : {
              imported: {
                status: duplicate ? ('duplicate' as const) : resolvedLine.status,
                name: line.ingredient ?? null,
                text: line.text ?? null,
                proposal: resolvedLine.proposal,
                suggestions: resolvedLine.suggestions,
              },
            }),
      }
    }),
  }
}

/**
 * What the form would save, as a comparable string. Ingredient lines collapse
 * to their saved values so the throwaway React key is not mistaken for an edit.
 */
function signature(state: InitialState): string {
  return JSON.stringify({
    ...state,
    lines: state.lines.map((line) => [
      line.ingredient?.id,
      line.amount,
      line.displayAmount,
      line.displayUnit,
      line.note,
    ]),
  })
}

/**
 * Free-text field with suggestions. Free text is deliberate: the backend stores
 * these as text, and the option list comes from config.yml plus what is already
 * in use, so a new value needs no schema change.
 */
function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder: string
}) {
  const id = `field-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-label font-medium text-ink-soft">
        {label}
      </label>
      <input
        id={id}
        list={`${id}-list`}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-[var(--radius-field)] border border-line-strong bg-surface px-3 text-sm text-ink placeholder:text-ink-faint/70 hover:border-ink-faint focus:border-accent"
      />
      <datalist id={`${id}-list`}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </div>
  )
}